import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Tabs, TabList, Tab, TabPanels, TabPanel } from "@carbon/react";
import "./bid.scss";

type Props = {
  /** Tab 1 — the order summary, or (once awarded) the adjudication result. */
  adjudication: ReactNode;
  /** Tab 2 — the suppliers that participated and every offer received. */
  suppliers: ReactNode;
  /** Tab 3 — the audit trail (activity log). */
  audit: ReactNode;
  /** Tab-1 label: "Resumen" while the order is still running, "Adjudicación" once awarded. */
  firstTabLabel?: string;
};

/**
 * The buyer order detail split into three tabs so the long single scroll (summary/adjudication +
 * tiles + suppliers + received offers + activity log) becomes legible. Used for EVERY order state
 * (the first tab is a plain summary while quoting, the adjudication result once awarded). Shared by
 * {@link BuyerBid} and {@link JointBuyerBid}; each passes its own already-composed slot content
 * (simple vs joint pieces differ), so this component only owns the tab shell.
 */
const BidAwardedTabs = ({
  adjudication,
  suppliers,
  audit,
  firstTabLabel,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Tabs>
      <TabList aria-label={t("tenders.data.awardedTabsAria")} contained>
        <Tab>{firstTabLabel ?? t("tenders.data.tabAdjudication")}</Tab>
        <Tab>{t("tenders.data.tabSuppliers")}</Tab>
        <Tab>{t("tenders.data.tabAudit")}</Tab>
      </TabList>
      <TabPanels>
        <TabPanel className="bid-tabpanel">{adjudication}</TabPanel>
        <TabPanel className="bid-tabpanel">{suppliers}</TabPanel>
        <TabPanel className="bid-tabpanel">{audit}</TabPanel>
      </TabPanels>
    </Tabs>
  );
};

export default BidAwardedTabs;
